import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { axiosInstance } from '../../helpers'
import {
    SignableDoc,
    SignableDocBuilderToken,
    SignableDocCopyResult,
    SignableDocApplicantDataField,
    SignableDocPropertyDataField,
    SignableDocFieldMapping,
    SignableDocMappingKind,
    SignableDocPreviewConfig,
    SignableDocStatus,
    SignableDocTemplateField,
    SignableDocType,
    SignableDocValueSource,
} from '../../models/Outreach/types'
import {
    seasonDetailKey,
    SEASONS_LIST_KEY,
} from '../../containers/LeasingSeason/hooks/leasingSeasonKeys'

export interface SignableDocFilters {
    document_type?: SignableDocType
    status?: SignableDocStatus
    unit_config_id?: number
    season_id?: number
    doc_role?: string
    catalog_template_key?: string
}

const signableDocsKey = ['signable-docs'] as const

function buildQueryParams(filters?: SignableDocFilters) {
    const params = new URLSearchParams()
    if (filters?.document_type)
        params.set('document_type', filters.document_type)
    if (filters?.status) params.set('status', filters.status)
    if (filters?.unit_config_id)
        params.set('unit_config_id', String(filters.unit_config_id))
    if (filters?.season_id) params.set('season_id', String(filters.season_id))
    if (filters?.doc_role) params.set('doc_role', filters.doc_role)
    if (filters?.catalog_template_key)
        params.set('catalog_template_key', filters.catalog_template_key)
    const qs = params.toString()
    return qs ? `?${qs}` : ''
}

export function useSignableDocs(filters?: SignableDocFilters) {
    const queryClient = useQueryClient()

    const docsQuery = useQuery({
        queryKey: [...signableDocsKey, filters],
        queryFn: async () => {
            const { data } = await axiosInstance.get<SignableDoc[]>(
                `/outreach/signable-docs/${buildQueryParams(filters)}`,
            )
            return data
        },
        staleTime: 30_000,
    })

    const createDocMutation = useMutation({
        mutationFn: async (payload: {
            name: string
            document_type: SignableDocType
            unit_config_id?: number | null
        }) => {
            const { data } = await axiosInstance.post<SignableDoc>(
                '/outreach/signable-docs/',
                { ...payload, status: 'DRAFT' },
            )
            return data
        },
        onError: (error: { response?: { data?: { error?: string } } }) => {
            toast.error(
                error?.response?.data?.error ?? 'Failed to create signable doc',
            )
        },
    })

    const patchDocMutation = useMutation({
        mutationFn: async ({
            id,
            payload,
        }: {
            id: number
            payload: Partial<{
                name: string
                document_type: SignableDocType
                unit_config_id: number | null
                status: SignableDocStatus
                docuseal_template_id: number
                docuseal_slug: string | null
                enabled_fields: string[]
                field_mappings: Record<string, SignableDocFieldMapping>
            }>
        }) => {
            const { data } = await axiosInstance.patch<SignableDoc>(
                `/outreach/signable-docs/${id}/`,
                payload,
            )
            return data
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: signableDocsKey })
            if (variables.payload.status === 'ARCHIVED') {
                queryClient.invalidateQueries({ queryKey: [SEASONS_LIST_KEY] })
            }
            if (variables.id) {
                queryClient.invalidateQueries({
                    queryKey: [...signableDocsKey, variables.id],
                })
            }
        },
        onError: (error: { response?: { data?: { error?: string } } }) => {
            toast.error(
                error?.response?.data?.error ?? 'Failed to update signable doc',
            )
        },
    })

    const copyDocMutation = useMutation({
        mutationFn: async (payload: {
            source_id: number
            name: string
            season_id?: number | null
        }) => {
            const { data } = await axiosInstance.post<SignableDocCopyResult>(
                '/outreach/signable-docs/copy/',
                payload,
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: signableDocsKey })
        },
        onError: (error: { response?: { data?: { error?: string } } }) => {
            toast.error(
                error?.response?.data?.error ?? 'Failed to copy signable doc',
            )
        },
    })

    const assignSeasonMutation = useMutation({
        mutationFn: async ({
            docId,
            seasonId,
        }: {
            docId: number
            seasonId: number
        }) => {
            const { data } = await axiosInstance.post<SignableDoc>(
                `/outreach/signable-docs/${docId}/assign-season/`,
                { season_id: seasonId },
            )
            return data
        },
        onSuccess: (_, variables) => {
            toast.success('Season assignment updated')
            queryClient.invalidateQueries({ queryKey: signableDocsKey })
            queryClient.invalidateQueries({
                queryKey: seasonDetailKey(variables.seasonId),
            })
            queryClient.invalidateQueries({ queryKey: [SEASONS_LIST_KEY] })
        },
        onError: (error: { response?: { data?: { error?: string } } }) => {
            toast.error(
                error?.response?.data?.error ?? 'Failed to assign season',
            )
        },
    })

    const removeFromSeasonMutation = useMutation({
        mutationFn: async (docId: number) => {
            const { data } = await axiosInstance.post<{
                id: number
                status: SignableDocStatus
                removed_from_season: boolean
            }>(`/outreach/signable-docs/${docId}/remove-from-season/`)
            return data
        },
        onSuccess: (_, docId) => {
            queryClient.invalidateQueries({ queryKey: signableDocsKey })
            queryClient.invalidateQueries({ queryKey: [SEASONS_LIST_KEY] })
            queryClient.invalidateQueries({
                queryKey: [...signableDocsKey, docId],
            })
        },
        onError: (error: { response?: { data?: { error?: string } } }) => {
            toast.error(
                error?.response?.data?.error ?? 'Failed to remove document',
            )
        },
    })

    const testSendMutation = useMutation({
        mutationFn: async (docId: number) => {
            const { data } = await axiosInstance.post<{
                docuseal_submission_id: number
                sent_to: string
            }>(`/outreach/signable-docs/${docId}/test-send/`)
            return data
        },
        onSuccess: (result) => {
            toast.success(`Test sent to ${result.sent_to}`)
        },
        onError: (error: { response?: { data?: { error?: string } } }) => {
            toast.error(error?.response?.data?.error ?? 'Failed to send test')
        },
    })

    return {
        docs: docsQuery.data ?? [],
        isLoading: docsQuery.isLoading,
        refetch: docsQuery.refetch,
        createDoc: createDocMutation.mutateAsync,
        isCreating: createDocMutation.isLoading,
        patchDoc: patchDocMutation.mutateAsync,
        isPatching: patchDocMutation.isLoading,
        removeFromSeason: removeFromSeasonMutation.mutateAsync,
        isRemovingFromSeason: removeFromSeasonMutation.isLoading,
        copyDoc: copyDocMutation.mutateAsync,
        isCopying: copyDocMutation.isLoading,
        assignSeason: assignSeasonMutation.mutateAsync,
        isAssigningSeason: assignSeasonMutation.isLoading,
        testSend: testSendMutation.mutateAsync,
        isTestSending: testSendMutation.isLoading,
    }
}

export function useSignableDoc(docId: number | undefined) {
    return useQuery({
        queryKey: [...signableDocsKey, docId],
        queryFn: async () => {
            const { data } = await axiosInstance.get<SignableDoc>(
                `/outreach/signable-docs/${docId}/`,
            )
            return data
        },
        enabled: docId != null,
        staleTime: 30_000,
    })
}

export function useSignableDocPreviewConfig(docId: number | undefined) {
    return useQuery({
        queryKey: ['signable-doc-preview', docId],
        queryFn: async () => {
            const { data } = await axiosInstance.get<SignableDocPreviewConfig>(
                `/outreach/signable-docs/${docId}/preview-config/`,
            )
            return data
        },
        enabled: docId != null,
        staleTime: 60_000,
        retry: 1,
    })
}

/** Builder JWT for read-only template preview (field boxes with names). */
export function useSignableDocBuilderPreviewToken(
    docId: number | undefined,
    templateId: number | null | undefined,
) {
    return useQuery({
        queryKey: ['signable-doc-builder-preview', docId, templateId],
        queryFn: async () => {
            return requestBuilderToken(docId!, {
                template_id: templateId!,
            })
        },
        enabled: docId != null && templateId != null,
        staleTime: 60_000,
        retry: 1,
    })
}

export interface DocuSealBuilderFieldDef {
    name: string
    type: string
    role: string
}

export interface DocuSealFieldDefinition {
    name: string
    description: string
    category?: string
    role?: string
    field_type?: string
    lead_prefill?: boolean
    lead_sync?: boolean
    readonly_at_send?: boolean
    optional?: boolean
}

export interface SuggestedPrefillFieldsResponse {
    fields: string[]
    builder_fields: DocuSealBuilderFieldDef[]
    field_definitions: DocuSealFieldDefinition[]
    field_definitions_detailed?: DocuSealFieldDefinition[]
    field_categories?: Record<string, string>
}

export interface StandardSignableTemplate {
    key: string
    name: string
    document_type: SignableDocType
    description: string
    field_count: number
    available: boolean
    pdf_ready?: boolean
    pdf_configured?: boolean
    pdf_s3_key?: string | null
    source: 'pdf' | 'docuseal_template' | null
}

export interface StandardSignableTemplatesResponse {
    templates: StandardSignableTemplate[]
    field_categories: Record<string, string>
}

export interface ProvisionStandardResult extends SignableDoc {
    template_id?: number
    template_slug?: string
}

export function useSuggestedPrefillFields() {
    return useQuery({
        queryKey: ['signable-doc-suggested-fields'],
        queryFn: async () => {
            const {
                data,
            } = await axiosInstance.get<SuggestedPrefillFieldsResponse>(
                '/outreach/signable-docs/suggested-fields/',
            )
            return data
        },
        staleTime: Infinity,
    })
}

export function useStandardSignableTemplates() {
    return useQuery({
        queryKey: ['signable-doc-standard-templates'],
        queryFn: async () => {
            const {
                data,
            } = await axiosInstance.get<StandardSignableTemplatesResponse>(
                '/outreach/signable-docs/standard-templates/',
            )
            return data
        },
        staleTime: 60_000,
    })
}

export async function provisionStandardSignableDoc(payload: {
    template_key: string
    name?: string
    season_id?: number | null
}) {
    const { data } = await axiosInstance.post<ProvisionStandardResult>(
        '/outreach/signable-docs/provision-standard/',
        payload,
    )
    return data
}

export interface SignableDocCatalogEntry {
    template_key: string
    source: string
    state: string
    doc_role: string
    display_name: string
    document_type: SignableDocType
    description: string
    pipeline_eligible: boolean
    version: number
    field_count: number
    available: boolean
    pdf_ready: boolean
    pdf_configured: boolean
    source_type: string | null
}

export function useSignableDocCatalog(filters?: {
    state?: string
    doc_role?: string
    pipeline_eligible?: boolean
}) {
    return useQuery({
        queryKey: ['signable-doc-catalog', filters],
        queryFn: async () => {
            const params = new URLSearchParams()
            if (filters?.state) params.set('state', filters.state)
            if (filters?.doc_role) params.set('doc_role', filters.doc_role)
            if (filters?.pipeline_eligible !== undefined) {
                params.set(
                    'pipeline_eligible',
                    String(filters.pipeline_eligible),
                )
            }
            const qs = params.toString()
            const { data } = await axiosInstance.get<SignableDocCatalogEntry[]>(
                `/outreach/signable-doc-catalog/${qs ? `?${qs}` : ''}`,
            )
            return data
        },
        staleTime: 60_000,
    })
}

export async function provisionSignableDocFromCatalog(payload: {
    catalog_template_key: string
    name?: string
    season_id?: number | null
}) {
    const { data } = await axiosInstance.post<ProvisionStandardResult>(
        '/outreach/signable-docs/provision-catalog/',
        payload,
    )
    return data
}

export async function promoteCompanyDocumentToSignableDoc(payload: {
    company_document_id: number
    document_type?: SignableDocType
    name?: string
}) {
    const { data } = await axiosInstance.post<SignableDoc>(
        '/outreach/signable-docs/from-company-document/',
        payload,
    )
    return data
}

export async function requestUploadUrl(
    docId: number,
    filename: string,
    contentType?: string,
) {
    const { data } = await axiosInstance.post<{
        s3_key: string
        upload_url: string
        content_type: string
        bucket: string
    }>(`/outreach/signable-docs/${docId}/upload-url/`, {
        filename,
        content_type: contentType,
    })
    return data
}

export async function completeUpload(docId: number, s3Key: string) {
    const { data } = await axiosInstance.post<{ document_url: string }>(
        `/outreach/signable-docs/${docId}/upload-complete/`,
        { s3_key: s3Key },
    )
    return data
}

export async function requestBuilderToken(
    docId: number,
    payload: { document_urls?: string[]; template_id?: number },
) {
    const { data } = await axiosInstance.post<SignableDocBuilderToken>(
        `/outreach/signable-docs/${docId}/builder-token/`,
        payload,
    )
    return data
}

export async function saveBuilderDraft(
    docId: number,
    payload: { docuseal_template_id: number; docuseal_slug?: string },
) {
    const { data } = await axiosInstance.patch<SignableDoc>(
        `/outreach/signable-docs/${docId}/`,
        {
            docuseal_template_id: payload.docuseal_template_id,
            docuseal_slug: payload.docuseal_slug ?? null,
        },
    )
    return data
}

export async function completeBuilder(
    docId: number,
    payload: { docuseal_template_id: number; docuseal_slug?: string },
) {
    const { data } = await axiosInstance.post<SignableDoc>(
        `/outreach/signable-docs/${docId}/complete-builder/`,
        payload,
    )
    return data
}

export function useSignableDocValueSources() {
    return useQuery({
        queryKey: ['signable-doc-value-sources'],
        queryFn: async () => {
            const { data } = await axiosInstance.get<{
                sources: SignableDocValueSource[]
                kinds?: SignableDocMappingKind[]
                applicant_data_fields?: SignableDocApplicantDataField[]
                property_data_fields?: SignableDocPropertyDataField[]
            }>('/outreach/signable-docs/value-sources/')
            return {
                sources: data.sources ?? [],
                kinds: data.kinds ?? [],
                applicant_data_fields: data.applicant_data_fields ?? [],
                property_data_fields: data.property_data_fields ?? [],
            }
        },
        staleTime: 60_000,
    })
}

export function useSignableDocTemplateFields(
    docId: number | undefined,
    enabled = true,
) {
    return useQuery({
        queryKey: ['signable-doc-template-fields', docId],
        queryFn: async () => {
            const { data } = await axiosInstance.get<{
                fields: SignableDocTemplateField[]
                field_mappings: Record<string, SignableDocFieldMapping>
            }>(`/outreach/signable-docs/${docId}/template-fields/`)
            return data
        },
        enabled: enabled && docId != null,
        staleTime: 15_000,
    })
}

export async function uploadFileToS3(
    uploadUrl: string,
    file: File,
    contentType: string,
) {
    const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
            'Content-Type': contentType,
        },
    })
    if (!response.ok) {
        throw new Error(`S3 upload failed (${response.status})`)
    }
}
